<template>
  <Teleport to="body">
    <div v-if="open" class="lead-overlay" @click.self="$emit('close')">
      <div class="lead-modal">
        <button class="lead-close" @click="$emit('close')">×</button>

        <div v-if="submitted" class="lead-success">
          <div class="lead-success-icon">✓</div>
          <h3>{{ t('lead.thankYou') }}</h3>
          <p>{{ t('lead.successMessage') }}</p>
        </div>

        <template v-else>
          <h3 class="lead-title">{{ t('lead.title') }}</h3>
          <p class="lead-subtitle">{{ t('lead.subtitle') }}</p>

          <form @submit.prevent="submitLead" class="lead-form">
            <input
              v-model="form.name"
              type="text"
              :placeholder="t('lead.name')"
              class="lead-input"
            />
            <input
              v-model="form.email"
              type="email"
              :placeholder="t('lead.email')"
              class="lead-input"
              required
            />
            <input
              v-model="form.phone"
              type="tel"
              :placeholder="t('lead.phone')"
              class="lead-input"
            />
            <textarea
              v-model="form.message"
              :placeholder="t('lead.message')"
              class="lead-input lead-textarea"
              rows="3"
            ></textarea>

            <p v-if="error" class="lead-error">{{ error }}</p>

            <button type="submit" class="lead-submit" :disabled="submitting">
              {{ submitting ? '...' : t('lead.send') }}
            </button>
          </form>
        </template>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps<{
  open: boolean
  tourUuid: string
  apiUrl: string
  analyticsContext?: {
    session_id?: string
    visitor_id?: string
    referrer?: string
  } | null
}>()

defineEmits(['close'])

const form = reactive({
  name: '',
  email: '',
  phone: '',
  message: '',
})

const submitting = ref(false)
const submitted = ref(false)
const error = ref('')

async function submitLead() {
  if (!form.email) return
  submitting.value = true
  error.value = ''

  try {
    const res = await fetch(`${props.apiUrl}/api/leads/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tour_uuid: props.tourUuid,
        name: form.name || null,
        email: form.email,
        phone: form.phone || null,
        message: form.message || null,
        session_id: props.analyticsContext?.session_id || null,
        visitor_id: props.analyticsContext?.visitor_id || null,
        referrer: props.analyticsContext?.referrer || document.referrer || null,
      }),
    })

    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.error || 'Failed to submit')
    }

    submitted.value = true
  } catch (e: any) {
    error.value = e.message || 'Something went wrong. Please try again.'
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.lead-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.lead-modal {
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  max-width: 420px;
  width: 100%;
  position: relative;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
}
.lead-close {
  position: absolute;
  top: 12px;
  right: 16px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  line-height: 1;
}
.lead-close:hover { color: #333; }
.lead-title {
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 4px;
}
.lead-subtitle {
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
}
.lead-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.lead-input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
}
.lead-input:focus {
  border-color: #3b82f6;
}
.lead-textarea {
  resize: vertical;
  min-height: 60px;
}
.lead-error {
  color: #ef4444;
  font-size: 13px;
  margin: 0;
}
.lead-submit {
  padding: 12px;
  background: #3b82f6;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}
.lead-submit:hover { background: #2563eb; }
.lead-submit:disabled { opacity: 0.6; cursor: not-allowed; }
.lead-success {
  text-align: center;
  padding: 20px 0;
}
.lead-success-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #22c55e;
  color: #fff;
  font-size: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}
.lead-success h3 {
  font-size: 20px;
  color: #1a1a1a;
  margin-bottom: 8px;
}
.lead-success p {
  color: #666;
  font-size: 14px;
}
</style>
